const normalizeCandidate = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed.length ? trimmed : '';
};

export const deriveBusinessDisplayName = (business) => {
  if (!business) return '';

  const candidates = [
    normalizeCandidate(business.businessName),
    normalizeCandidate(business.legalName),
    normalizeCandidate(business.tradeName),
    normalizeCandidate(business.brandName),
    normalizeCandidate(business.displayName),
  ].filter(Boolean);

  if (candidates.length) {
    return candidates[0];
  }

  const identifier = normalizeCandidate(business.businessId) || normalizeCandidate(business.id);
  if (!identifier) {
    return 'Business';
  }

  const shortId = identifier.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();
  return shortId ? `Business ${shortId}` : 'Business';
};

const businessNameScore = (business) => {
  if (!business) return 0;
  const name = deriveBusinessDisplayName(business);
  if (!name) return 0;
  // Treat fallback names as having minimal score so entries with real names win.
  const fallbackPrefix = 'Business ';
  if (name === 'Business' || name.startsWith(fallbackPrefix)) {
    return 1;
  }
  return name.length + 10;
};

export const dedupeBusinesses = (businesses = []) => {
  const byId = new Map();
  businesses.forEach((business) => {
    if (!business || !business.businessId) {
      return;
    }
    const existing = byId.get(business.businessId);
    if (!existing) {
      byId.set(business.businessId, business);
      return;
    }

    if (businessNameScore(business) > businessNameScore(existing)) {
      byId.set(business.businessId, business);
    }
  });
  return Array.from(byId.values());
};

const normalizeKey = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

export const BUSINESS_KIND_OPTIONS = [
  {
    value: 'FOOD_SERVICE',
    label: 'Food Service',
    categories: [
      { value: 'PERMANENT STALL HOLDER', label: 'Permanent Stall Holder' },
      { value: 'TEMPORARY STALL HOLDER', label: 'Temporary Stall Holder' },
      { value: 'FOOD STALLS (ARRANGMENTS IN RELIGIOUS GATHERINGS)', label: 'Food Stalls (Arrangements in Religious Gatherings)' },
      { value: 'HAWKER - ITINERANT', label: 'Hawker - Itinerant' },
      { value: 'HAWKER (ITINERANT / MOBILE FOOD VENDOR)', label: 'Hawker (Itinerant / Mobile Food Vendor)' },
      { value: 'PETTY RETAILER OF SNACKS / TEA SHOPS', label: 'Petty Retailer of Snacks / Tea Shops' },
      { value: 'HOTEL', label: 'Hotel' },
      { value: 'RESTAURANT', label: 'Restaurant' },
      { value: 'FOOD VENDING ESTABLISHMENT', label: 'Food Vending Establishment' },
      { value: 'FOOD VENDING AGENCIES', label: 'Food Vending Agencies' },
      { value: 'QUICK SERVICE RESTAURANT (QSR)', label: 'Quick Service Restaurant (QSR)' },
      { value: 'CATERER', label: 'Caterer' },
      { value: 'CLUB', label: 'Club' },
      { value: 'CANTEEN', label: 'Canteen' },
      { value: 'MID DAY MEAL - CANTEEN', label: 'Mid Day Meal - Canteen' },
      { value: 'MID DAY MEAL - CATERER', label: 'Mid Day Meal - Caterer' },
      { value: 'FOOD SERVICE OPERATION', label: 'Food Service Operation' },
      { value: 'HOME BASED CANTEEN / DABBA WALAS', label: 'Home Based Canteen / Dabba Walas' },
      { value: 'DHABA', label: 'Dhaba' },
      { value: 'CLOUD KITCHEN', label: 'Cloud Kitchen' },
      { value: 'BOARDING HOUSE SERVING FOOD / MESS', label: 'Boarding House Serving Food / Mess' },
      { value: 'CATERING SERVICES IN CENTRAL GOVT. AGENCIES', label: 'Catering Services in Central Govt. Agencies' },
      { value: 'CATERING SERVICES AT AIRPORT', label: 'Catering Services at Airport' },
      { value: 'CATERING SERVICES AT SEAPORT', label: 'Catering Services at Seaport' },
      { value: 'CATERING SERVICES AT RAILWAYS', label: 'Catering Services at Railways' },
    ],
  },
  {
    value: 'MANUFACTURING',
    label: 'Manufacturing',
    categories: [
      { value: 'GENERAL MANUFACTURER', label: 'General Manufacturer' },
      { value: 'MANUFACTURER - REPACKER', label: 'Manufacturer - Repacker' },
      { value: 'MANUFACTURER - RELABELLER', label: 'Manufacturer - Relabeller' },
      { value: 'DAIRY UNITS', label: 'Dairy Units' },
      { value: 'VEGETABLE OIL PROCESSING UNITS', label: 'Vegetable Oil Processing Units' },
      { value: 'SLAUGHTERING UNITS', label: 'Slaughtering Units' },
      { value: 'MEAT PROCESSING UNITS', label: 'Meat Processing Units' },
      { value: 'FISH PROCESSING UNITS', label: 'Fish Processing Units' },
    ],
  },
  {
    value: 'TRADE',
    label: 'Trade',
    categories: [
      { value: 'WHOLESALER', label: 'Wholesaler' },
      { value: 'RETAILER', label: 'Retailer' },
      { value: 'DISTRIBUTOR / SUPPLIER', label: 'Distributor / Supplier' },
    ],
  },
  {
    value: 'E_COMMERCE',
    label: 'E-commerce',
    categories: [
      { value: 'E - COMMERCE', label: 'E - Commerce' },
    ],
  },
  {
    value: 'STORAGE',
    label: 'Storage',
    categories: [
      { value: 'STORAGE UNIT (COLD / REFRIGERATED)', label: 'Storage Unit (Cold / Refrigerated)' },
      { value: 'STORAGE (CONTROLLED ATMOSPHERE + COLD)', label: 'Storage (Controlled Atmosphere + Cold)' },
      { value: 'STORAGE (EXCEPT CONTROLLED ATMOSPHERE AND COLD)', label: 'Storage (Except Controlled Atmosphere and Cold)' },
    ],
  },
  {
    value: 'TRANSPORT',
    label: 'Transport',
    categories: [
      { value: 'TRANSPORTER', label: 'Transporter' },
    ],
  },
  {
    value: 'IMPORTS',
    label: 'Imports',
    categories: [
      { value: 'IMPORTER', label: 'Importer' },
    ],
  },
  {
    value: 'EXPORT',
    label: 'Export',
    categories: [
      { value: 'EXPORTER', label: 'Exporter' },
    ],
  },
];

const CATEGORY_TO_KIND = BUSINESS_KIND_OPTIONS.reduce((acc, kind) => {
  kind.categories.forEach((cat) => {
    acc[normalizeKey(cat.value)] = kind.value;
  });
  return acc;
}, {});

export const findKindByCategoryValue = (categoryValue) => CATEGORY_TO_KIND[normalizeKey(categoryValue)] || '';

export const getBusinessKindLabel = (value) => {
  const match = BUSINESS_KIND_OPTIONS.find((kind) => kind.value === value);
  return match?.label || value || 'N/A';
};

export const getBusinessCategoryLabel = (value) => {
  const normalized = normalizeKey(value);
  for (const kind of BUSINESS_KIND_OPTIONS) {
    const category = kind.categories.find((cat) => normalizeKey(cat.value) === normalized);
    if (category) {
      return category.label;
    }
  }
  return value || 'N/A';
};
